import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createReview, clearError, clearSuccess } from '../redux/slices/reviewSlice';

const CreateReview = ({ packageId, tourType: initialTourType }) => {
    const dispatch = useDispatch();
    const { loading, error: reduxError, success } = useSelector((state) => state.reviews);
    console.log("Redux State:", { success, reduxError });

    const [rating, setRating] = useState(0);
    const [comment, setComment] = useState('');
    const [mediaFiles, setMediaFiles] = useState([]);
    const [previews, setPreviews] = useState([]);
    const [tourType, setTourType] = useState(initialTourType || '');
    const [errors, setErrors] = useState({ rating: '', comment: '', tourType: '' });
    const [hoverRating, setHoverRating] = useState(0);
    const [submissionError, setSubmissionError] = useState(null);

    // Predefined tour type options
    const tourTypeOptions = [
        { value: 'Domestic', label: 'Domestic' },
        { value: 'International', label: 'International' },
        { value: 'Short', label: 'Short Trip' },
        { value: 'Customised', label: 'Customised' },
    ];

    // Validate form inputs
    const validateForm = () => {
        let isValid = true;
        const newErrors = { rating: '', comment: '', tourType: '' };

        if (!rating) {
            newErrors.rating = 'Please select a rating';
            isValid = false;
        }
        if (!comment.trim()) {
            newErrors.comment = 'Please enter a comment';
            isValid = false;
        }
        if (!tourType) {
            newErrors.tourType = 'Please select a tour type';
            isValid = false;
        }

        setErrors(newErrors);
        return isValid;
    };

    // Handle file input change with max 6 files restriction
    const handleFileChange = (e) => {
        const files = Array.from(e.target.files);
        const validTypes = ['image/jpeg', 'image/png', 'image/jpg', 'video/mp4'];
        const maxFiles = 6;

        const newFiles = files.filter((file) => validTypes.includes(file.type));
        if (newFiles.length !== files.length) {
            alert('Only JPEG, PNG, JPG images and MP4 videos are allowed.');
        }

        if (mediaFiles.length + newFiles.length > maxFiles) {
            alert(`You can upload a maximum of ${maxFiles} files.`);
            return;
        }

        const newPreviews = newFiles.map((file) => ({
            url: URL.createObjectURL(file),
            type: file.type.startsWith('image/') ? 'image' : 'video',
        }));

        setMediaFiles((prev) => [...prev, ...newFiles]);
        setPreviews((prev) => [...prev, ...newPreviews]);
    };

    // Remove a file from the list
    const removeFile = (index) => {
        setMediaFiles((prev) => prev.filter((_, i) => i !== index));
        setPreviews((prev) => prev.filter((_, i) => i !== index));
    };

    // Handle tour type selection
    const handleTourTypeSelect = (value) => {
        setTourType(value);
        setErrors((prev) => ({ ...prev, tourType: '' }));
    };

    // Handle form submission
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        setSubmissionError(null); // Clear previous submission errors

        const mediaData = previews.map((preview, index) => ({
            type: preview.type,
            alt: `User uploaded ${preview.type} ${index + 1}`,
        }));

        try {
            await dispatch(
                createReview({
                    packageId,
                    tourType,
                    rating,
                    comment,
                    mediaFiles,
                    mediaData,
                })
            ).unwrap();
            // Clear form after success
            setRating(0);
            setComment('');
            setMediaFiles([]);
            setPreviews([]);
            setTourType('');
            setErrors({ rating: '', comment: '', tourType: '' });
        } catch (err) {
            console.error("Submission error:", err);
            // Handle various error formats
            const errorMessage =
                typeof err === 'string'
                    ? err
                    : err.message ||
                    err.error ||
                    err.response?.data?.message ||
                    'Failed to submit review. Please try again.';
            setSubmissionError(errorMessage);
            console.log("Set submissionError:", errorMessage);
        }
    };

    // Clean up object URLs for previews
    useEffect(() => {
        return () => {
            previews.forEach((preview) => {
                if (preview.url && typeof preview.url === 'string') {
                    URL.revokeObjectURL(preview.url);
                }
            });
        };
    }, [previews]);


    // Clear success/error messages after display
    useEffect(() => {
        if (success) {
            const timer = setTimeout(() => {
                dispatch(clearSuccess());
            }, 5000);
            return () => clearTimeout(timer);
        }
        if (reduxError || submissionError) {
            console.log("Rendering error message:", { reduxError, submissionError });
            const timer = setTimeout(() => {
                dispatch(clearError());
                setSubmissionError(null);
            }, 5000);
            return () => clearTimeout(timer);
        }
    }, [success, reduxError, submissionError, dispatch]);

    return (
        <div className="max-w-2xl mx-auto p-6 bg-custom-white rounded-lg shadow-md">
            {/* <h2 className="text-2xl font-bold text-custom-blue mb-6">Write a Review</h2> */}
            {/* Success/Error Messages */}
            <div aria-live="polite" className="relative z-10">
                {success && (
                    <div className="mb-4 p-3 bg-green-100 text-green-700 rounded-lg flex justify-between items-center">
                        <span>Review submitted successfully!</span>
                        <button
                            onClick={() => dispatch(clearSuccess())}
                            className="text-green-700 hover:text-green-900"
                        >
                            <svg
                                className="w-5 h-5"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    d="M6 18L18 6M6 6l12 12"
                                />
                            </svg>
                        </button>
                    </div>
                )}
                {(reduxError || submissionError) && (
                    <div
                        className="mb-4 p-3 bg-red-100 text-red-700 rounded-lg flex justify-between items-center"
                        role="alert"
                    >
                        <span>{reduxError || submissionError}</span>
                        <button
                            onClick={() => {
                                dispatch(clearError());
                                setSubmissionError(null);
                            }}
                            className="text-red-700 hover:text-red-900"
                        >
                            <svg
                                className="w-5 h-5"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    d="M6 18L18 6M6 6l12 12"
                                />
                            </svg>
                        </button>
                    </div>
                )}
            </div>
            <form onSubmit={handleSubmit} className="space-y-6">
                {/* Tour Type Selection */}
                <div>
                    <label className="block text-sm font-medium text-custom-blue mb-2">
                        Tour Type <span className="text-red-500">*</span>
                    </label>
                    <div className="flex flex-wrap gap-2">
                        {tourTypeOptions.map((option) => (
                            <button
                                key={option.value}
                                type="button"
                                onClick={() => handleTourTypeSelect(option.value)}
                                className={`px-4 py-2 rounded-lg border transition-colors ${tourType === option.value
                                    ? 'bg-custom-blue text-custom-white border-custom-blue'
                                    : 'bg-custom-white text-custom-blue border-gray-300 hover:bg-gray-100'
                                    } ${errors.tourType && !tourType ? 'border-red-500' : ''}`}
                            >
                                {option.label}
                            </button>
                        ))}
                    </div>
                    {errors.tourType && (
                        <p className="text-red-500 text-sm mt-1">{errors.tourType}</p>
                    )}
                </div>

                {/* Rating */}
                <div>
                    <label className="block text-sm font-medium text-custom-blue mb-2">
                        Rating <span className="text-red-500">*</span>
                    </label>
                    <div className="flex items-center space-x-1">
                        {[1, 2, 3, 4, 5].map((star) => (
                            <button
                                key={star}
                                type="button"
                                onClick={() => setRating(star)}
                                onMouseEnter={() => setHoverRating(star)}
                                onMouseLeave={() => setHoverRating(0)}
                                className="focus:outline-none"
                            >
                                <svg
                                    className={`w-8 h-8 ${(hoverRating || rating) >= star
                                        ? 'text-custom-yellow fill-current'
                                        : 'text-gray-300'
                                        }`}
                                    viewBox="0 0 20 20"
                                >
                                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                </svg>
                            </button>
                        ))}
                    </div>
                    {errors.rating && (
                        <p className="text-red-500 text-sm mt-1">{errors.rating}</p>
                    )}
                </div>

                {/* Comment */}
                <div>
                    <label
                        htmlFor="comment"
                        className="block text-sm font-medium text-custom-blue mb-2"
                    >
                        Comment <span className="text-red-500">*</span>
                    </label>
                    <textarea
                        id="comment"
                        value={comment}
                        onChange={(e) => setComment(e.target.value)}
                        rows="4"
                        className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-custom-blue focus:outline-none ${errors.comment ? 'border-red-500' : 'border-gray-300'
                            }`}
                    />
                    {errors.comment && (
                        <p className="text-red-500 text-sm mt-1">{errors.comment}</p>
                    )}
                </div>

                {/* Media Upload */}
                <div>
                    <label
                        htmlFor="media"
                        className="block text-sm font-medium text-custom-blue mb-2"
                    >
                        Upload Photos or Videos (Optional)
                    </label>
                    <div className="flex items-center justify-center w-full">
                        <label
                            htmlFor="media"
                            className="flex flex-col items-center justify-center w-full h-32 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-custom-white hover:bg-gray-100"
                        >
                            <div className="flex flex-col items-center justify-center pt-5 pb-6">
                                <svg
                                    className="w-8 h-8 mb-2 text-gray-500"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth="2"
                                        d="M7 16V8m0 0l-3 3m3-3l3 3m6-7v12m0 0l-3-3m3 3l3-3"
                                    />
                                </svg>
                                <p className="mb-2 text-sm text-gray-500">
                                    <span className="font-semibold">Click to upload</span> or drag
                                    and drop
                                </p>
                                <p className="text-xs text-gray-500">
                                    JPEG, PNG, JPG, MP4 (max 6 files)
                                </p>
                            </div>
                            <input
                                id="media"
                                type="file"
                                accept="image/jpeg,image/png,image/jpg,video/mp4"
                                multiple
                                onChange={handleFileChange}
                                className="hidden"
                                disabled={mediaFiles.length >= 6}
                            />
                        </label>
                    </div>
                    {/* Media Previews */}
                    {previews.length > 0 && (
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mt-4">
                            {previews.map((preview, index) => (
                                <div key={index} className="relative">
                                    {preview.type === 'image' ? (
                                        <img
                                            src={preview.url}
                                            alt={`Preview ${index + 1}`}
                                            className="w-full h-32 object-cover rounded-lg"
                                        />
                                    ) : (
                                        <video
                                            src={preview.url}
                                            className="w-full h-32 object-cover rounded-lg"
                                            muted
                                        />
                                    )}
                                    <button
                                        type="button"
                                        onClick={() => removeFile(index)}
                                        className="absolute top-1 right-1 bg-red-500 text-custom-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-red-600"
                                    >
                                        <svg
                                            className="w-4 h-4"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth="2"
                                                d="M6 18L18 6M6 6l12 12"
                                            />
                                        </svg>
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Submit Button */}
                <button
                    type="submit"
                    disabled={loading}
                    className={`w-full py-3 px-4 rounded-lg text-custom-white font-semibold flex items-center justify-center ${loading ? 'bg-gray-400 cursor-not-allowed' : 'bg-custom-blue hover:bg-custom-blue/90'
                        }`}
                >
                    {loading ? (
                        <>
                            <svg
                                className="animate-spin h-5 w-5 mr-2 text-custom-white"
                                viewBox="0 0 24 24"
                            >
                                <circle
                                    className="opacity-25"
                                    cx="12"
                                    cy="12"
                                    r="10"
                                    stroke="currentColor"
                                    strokeWidth="4"
                                />
                                <path
                                    className="opacity-75"
                                    fill="currentColor"
                                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                                />
                            </svg>
                            Submitting...
                        </>
                    ) : (
                        'Submit Review'
                    )}
                </button>
            </form>
        </div>
    );
};

export default CreateReview;